/**
 * The rotation maths three.js applies to an Object3D, written out so the pure simulation can aim without three.js
 * (master plan porting rule 5). Every expression is three's own (r159 and r186 are identical here), in the same
 * order, so results agree to the last bit: Quaternion.setFromEuler (order 'YXZ'), Vector3.applyQuaternion, and the
 * translation and rotation rows of Matrix4.compose that Vector3.applyMatrix4 reads for an unparented object.
 */

/** A quaternion as [x, y, z, w]. */
export type Quat4 = [number, number, number, number];
/** A 3-vector as [x, y, z]. */
export type Vec3 = [number, number, number];

/** three.js Quaternion.setFromEuler for Euler(x, y, z, 'YXZ'); writes into `out`. */
export function quatFromEulerYXZ(x: number, y: number, z: number, out: Quat4): Quat4 {
  const c1 = Math.cos(x / 2);
  const c2 = Math.cos(y / 2);
  const c3 = Math.cos(z / 2);
  const s1 = Math.sin(x / 2);
  const s2 = Math.sin(y / 2);
  const s3 = Math.sin(z / 2);
  out[0] = s1 * c2 * c3 + c1 * s2 * s3;
  out[1] = c1 * s2 * c3 - s1 * c2 * s3;
  out[2] = c1 * c2 * s3 - s1 * s2 * c3;
  out[3] = c1 * c2 * c3 + s1 * s2 * s3;
  return out;
}

/** three.js Vector3.applyQuaternion: rotates (vx, vy, vz) by unit quaternion `q`; writes into `out`. */
export function rotateByQuat(q: Quat4, vx: number, vy: number, vz: number, out: Vec3): Vec3 {
  const qx = q[0];
  const qy = q[1];
  const qz = q[2];
  const qw = q[3];
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  out[0] = vx + qw * tx + qy * tz - qz * ty;
  out[1] = vy + qw * ty + qz * tx - qx * tz;
  out[2] = vz + qw * tz + qx * ty - qy * tx;
  return out;
}

/**
 * A point in an unparented object's local space, transformed to world space exactly as
 * `point.applyMatrix4(object.matrixWorld)` does after `matrixWorld = scene(identity) × compose(position, q, 1)`.
 */
export function localToWorld(
  q: Quat4,
  px: number,
  py: number,
  pz: number,
  lx: number,
  ly: number,
  lz: number,
  out: Vec3,
): Vec3 {
  const x = q[0];
  const y = q[1];
  const z = q[2];
  const w = q[3];
  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;
  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;
  // compose() (scale 1), then the scene's identity matrix in front: products with 1 and 0 leave every element
  // unchanged, so the world matrix equals the local one bit for bit.
  const e0 = 1 - (yy + zz);
  const e1 = xy + wz;
  const e2 = xz - wy;
  const e4 = xy - wz;
  const e5 = 1 - (xx + zz);
  const e6 = yz + wx;
  const e8 = xz + wy;
  const e9 = yz - wx;
  const e10 = 1 - (xx + yy);
  // applyMatrix4: the perspective divide is by exactly 1.
  out[0] = e0 * lx + e4 * ly + e8 * lz + px;
  out[1] = e1 * lx + e5 * ly + e9 * lz + py;
  out[2] = e2 * lx + e6 * ly + e10 * lz + pz;
  return out;
}
